/**
 * Utility function to format phone numbers with proper district code display
 * Example: +6531237151 -> (+65) 31237151
 */

/**
 * Formats a phone number to display the district code properly
 * @param {string} phoneNumber - The full phone number (e.g., "+6531237151")
 * @returns {string} - Formatted phone number (e.g., "(+65) 31237151")
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove any existing formatting
  const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');

  // Common country codes and their lengths
  const countryCodes = [
    { code: '+1', length: 1 },      // US/Canada
    { code: '+7', length: 1 },      // Russia/Kazakhstan
    { code: '+20', length: 2 },     // Egypt
    { code: '+27', length: 2 },     // South Africa
    { code: '+30', length: 2 },     // Greece
    { code: '+31', length: 2 },     // Netherlands
    { code: '+32', length: 2 },     // Belgium
    { code: '+33', length: 2 },     // France
    { code: '+34', length: 2 },     // Spain
    { code: '+36', length: 2 },     // Hungary
    { code: '+39', length: 2 },     // Italy
    { code: '+40', length: 2 },     // Romania
    { code: '+41', length: 2 },     // Switzerland
    { code: '+43', length: 2 },     // Austria
    { code: '+44', length: 2 },     // UK
    { code: '+45', length: 2 },     // Denmark
    { code: '+46', length: 2 },     // Sweden
    { code: '+47', length: 2 },     // Norway
    { code: '+48', length: 2 },     // Poland
    { code: '+49', length: 2 },     // Germany
    { code: '+51', length: 2 },     // Peru
    { code: '+52', length: 2 },     // Mexico
    { code: '+53', length: 2 },     // Cuba
    { code: '+54', length: 2 },     // Argentina
    { code: '+55', length: 2 },     // Brazil
    { code: '+56', length: 2 },     // Chile
    { code: '+57', length: 2 },     // Colombia
    { code: '+58', length: 2 },     // Venezuela
    { code: '+60', length: 2 },     // Malaysia
    { code: '+61', length: 2 },     // Australia
    { code: '+62', length: 2 },     // Indonesia
    { code: '+63', length: 2 },     // Philippines
    { code: '+64', length: 2 },     // New Zealand
    { code: '+65', length: 2 },     // Singapore
    { code: '+66', length: 2 },     // Thailand
    { code: '+81', length: 2 },     // Japan
    { code: '+82', length: 2 },     // South Korea
    { code: '+84', length: 2 },     // Vietnam
    { code: '+86', length: 2 },     // China
    { code: '+90', length: 2 },     // Turkey
    { code: '+91', length: 2 },     // India
    { code: '+92', length: 2 },     // Pakistan
    { code: '+93', length: 2 },     // Afghanistan
    { code: '+94', length: 2 },     // Sri Lanka
    { code: '+95', length: 2 },     // Myanmar
    { code: '+98', length: 2 },     // Iran
    { code: '+212', length: 3 },    // Morocco
    { code: '+213', length: 3 },    // Algeria
    { code: '+216', length: 3 },    // Tunisia
    { code: '+218', length: 3 },    // Libya
    { code: '+220', length: 3 },    // Gambia
    { code: '+221', length: 3 },    // Senegal
    { code: '+222', length: 3 },    // Mauritania
    { code: '+223', length: 3 },    // Mali
    { code: '+224', length: 3 },    // Guinea
    { code: '+225', length: 3 },    // Ivory Coast
    { code: '+226', length: 3 },    // Burkina Faso
    { code: '+227', length: 3 },    // Niger
    { code: '+228', length: 3 },    // Togo
    { code: '+229', length: 3 },    // Benin
    { code: '+230', length: 3 },    // Mauritius
    { code: '+231', length: 3 },    // Liberia
    { code: '+232', length: 3 },    // Sierra Leone
    { code: '+233', length: 3 },    // Ghana
    { code: '+234', length: 3 },    // Nigeria
    { code: '+235', length: 3 },    // Chad
    { code: '+236', length: 3 },    // Central African Republic
    { code: '+237', length: 3 },    // Cameroon
    { code: '+238', length: 3 },    // Cape Verde
    { code: '+239', length: 3 },    // São Tomé and Príncipe
    { code: '+240', length: 3 },    // Equatorial Guinea
    { code: '+241', length: 3 },    // Gabon
    { code: '+242', length: 3 },    // Republic of the Congo
    { code: '+243', length: 3 },    // Democratic Republic of the Congo
    { code: '+244', length: 3 },    // Angola
    { code: '+245', length: 3 },    // Guinea-Bissau
    { code: '+246', length: 3 },    // British Indian Ocean Territory
    { code: '+248', length: 3 },    // Seychelles
    { code: '+249', length: 3 },    // Sudan
    { code: '+250', length: 3 },    // Rwanda
    { code: '+251', length: 3 },    // Ethiopia
    { code: '+252', length: 3 },    // Somalia
    { code: '+253', length: 3 },    // Djibouti
    { code: '+254', length: 3 },    // Kenya
    { code: '+255', length: 3 },    // Tanzania
    { code: '+256', length: 3 },    // Uganda
    { code: '+257', length: 3 },    // Burundi
    { code: '+258', length: 3 },    // Mozambique
    { code: '+260', length: 3 },    // Zambia
    { code: '+261', length: 3 },    // Madagascar
    { code: '+262', length: 3 },    // Mayotte
    { code: '+263', length: 3 },    // Zimbabwe
    { code: '+264', length: 3 },    // Namibia
    { code: '+265', length: 3 },    // Malawi
    { code: '+266', length: 3 },    // Lesotho
    { code: '+267', length: 3 },    // Botswana
    { code: '+268', length: 3 },    // Swaziland
    { code: '+269', length: 3 },    // Comoros
    { code: '+290', length: 3 },    // Saint Helena
    { code: '+291', length: 3 },    // Eritrea
    { code: '+297', length: 3 },    // Aruba
    { code: '+298', length: 3 },    // Faroe Islands
    { code: '+299', length: 3 },    // Greenland
    { code: '+350', length: 3 },    // Gibraltar
    { code: '+351', length: 3 },    // Portugal
    { code: '+352', length: 3 },    // Luxembourg
    { code: '+353', length: 3 },    // Ireland
    { code: '+354', length: 3 },    // Iceland
    { code: '+355', length: 3 },    // Albania
    { code: '+356', length: 3 },    // Malta
    { code: '+357', length: 3 },    // Cyprus
    { code: '+358', length: 3 },    // Finland
    { code: '+359', length: 3 },    // Bulgaria
    { code: '+370', length: 3 },    // Lithuania
    { code: '+371', length: 3 },    // Latvia
    { code: '+372', length: 3 },    // Estonia
    { code: '+373', length: 3 },    // Moldova
    { code: '+374', length: 3 },    // Armenia
    { code: '+375', length: 3 },    // Belarus
    { code: '+376', length: 3 },    // Andorra
    { code: '+377', length: 3 },    // Monaco
    { code: '+378', length: 3 },    // San Marino
    { code: '+380', length: 3 },    // Ukraine
    { code: '+381', length: 3 },    // Serbia
    { code: '+382', length: 3 },    // Montenegro
    { code: '+383', length: 3 },    // Kosovo
    { code: '+385', length: 3 },    // Croatia
    { code: '+386', length: 3 },    // Slovenia
    { code: '+387', length: 3 },    // Bosnia and Herzegovina
    { code: '+389', length: 3 },    // North Macedonia
    { code: '+420', length: 3 },    // Czech Republic
    { code: '+421', length: 3 },    // Slovakia
    { code: '+423', length: 3 },    // Liechtenstein
    { code: '+500', length: 3 },    // Falkland Islands
    { code: '+501', length: 3 },    // Belize
    { code: '+502', length: 3 },    // Guatemala
    { code: '+503', length: 3 },    // El Salvador
    { code: '+504', length: 3 },    // Honduras
    { code: '+505', length: 3 },    // Nicaragua
    { code: '+506', length: 3 },    // Costa Rica
    { code: '+507', length: 3 },    // Panama
    { code: '+508', length: 3 },    // Saint Pierre and Miquelon
    { code: '+509', length: 3 },    // Haiti
    { code: '+590', length: 3 },    // Guadeloupe
    { code: '+591', length: 3 },    // Bolivia
    { code: '+592', length: 3 },    // Guyana
    { code: '+593', length: 3 },    // Ecuador
    { code: '+594', length: 3 },    // French Guiana
    { code: '+595', length: 3 },    // Paraguay
    { code: '+596', length: 3 },    // Martinique
    { code: '+597', length: 3 },    // Suriname
    { code: '+598', length: 3 },    // Uruguay
    { code: '+599', length: 3 },    // Netherlands Antilles
    { code: '+670', length: 3 },    // East Timor
    { code: '+672', length: 3 },    // Australian External Territories
    { code: '+673', length: 3 },    // Brunei
    { code: '+674', length: 3 },    // Nauru
    { code: '+675', length: 3 },    // Papua New Guinea
    { code: '+676', length: 3 },    // Tonga
    { code: '+677', length: 3 },    // Solomon Islands
    { code: '+678', length: 3 },    // Vanuatu
    { code: '+679', length: 3 },    // Fiji
    { code: '+680', length: 3 },    // Palau
    { code: '+681', length: 3 },    // Wallis and Futuna
    { code: '+682', length: 3 },    // Cook Islands
    { code: '+683', length: 3 },    // Niue
    { code: '+684', length: 3 },    // American Samoa
    { code: '+685', length: 3 },    // Samoa
    { code: '+686', length: 3 },    // Kiribati
    { code: '+687', length: 3 },    // New Caledonia
    { code: '+688', length: 3 },    // Tuvalu
    { code: '+689', length: 3 },    // French Polynesia
    { code: '+690', length: 3 },    // Tokelau
    { code: '+691', length: 3 },    // Micronesia
    { code: '+692', length: 3 },    // Marshall Islands
    { code: '+850', length: 3 },    // North Korea
    { code: '+852', length: 3 },    // Hong Kong
    { code: '+853', length: 3 },    // Macau
    { code: '+855', length: 3 },    // Cambodia
    { code: '+856', length: 3 },    // Laos
    { code: '+880', length: 3 },    // Bangladesh
    { code: '+886', length: 3 },    // Taiwan
    { code: '+960', length: 3 },    // Maldives
    { code: '+961', length: 3 },    // Lebanon
    { code: '+962', length: 3 },    // Jordan
    { code: '+963', length: 3 },    // Syria
    { code: '+964', length: 3 },    // Iraq
    { code: '+965', length: 3 },    // Kuwait
    { code: '+966', length: 3 },    // Saudi Arabia
    { code: '+967', length: 3 },    // Yemen
    { code: '+968', length: 3 },    // Oman
    { code: '+970', length: 3 },    // Palestine
    { code: '+971', length: 3 },    // UAE
    { code: '+972', length: 3 },    // Israel
    { code: '+973', length: 3 },    // Bahrain
    { code: '+974', length: 3 },    // Qatar
    { code: '+975', length: 3 },    // Bhutan
    { code: '+976', length: 3 },    // Mongolia
    { code: '+977', length: 3 },    // Nepal
    { code: '+992', length: 3 },    // Tajikistan
    { code: '+993', length: 3 },    // Turkmenistan
    { code: '+994', length: 3 },    // Azerbaijan
    { code: '+995', length: 3 },    // Georgia
    { code: '+996', length: 3 },    // Kyrgyzstan
    { code: '+998', length: 3 },    // Uzbekistan
  ];

  // Sort country codes by length (longest first) to match the most specific code first
  const sortedCountryCodes = countryCodes.sort((a, b) => b.code.length - a.code.length);

  // Find the matching country code
  for (const countryCode of sortedCountryCodes) {
    if (cleanNumber.startsWith(countryCode.code)) {
      const phoneNumber = cleanNumber.substring(countryCode.code.length);
      return `(${countryCode.code}) ${phoneNumber}`;
    }
  }

  // If no country code is found, return the original number
  return cleanNumber;
};

/**
 * Formats a phone number for display in a table or list view
 * @param {string} phoneNumber - The full phone number
 * @returns {string} - Formatted phone number for display
 */
export const formatPhoneNumberForDisplay = (phoneNumber) => {
  return formatPhoneNumber(phoneNumber);
};

/**
 * Formats a phone number for input fields (removes formatting)
 * @param {string} phoneNumber - The formatted phone number
 * @returns {string} - Clean phone number for input
 */
export const cleanPhoneNumberForInput = (phoneNumber) => {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/[\s\-\(\)]/g, '');
};
